import { useEffect, useState } from 'react';

import { colors } from '@tempmee/tailwind';
import { Colors } from '../../../../themes/colors';
import { SolidCheckCircleIcon, SolidExclamationWarningAlertIcon } from '@tempmee/react-icons';
import { currencyFormat } from '~/utils/currencyFormat';

export const useRate = ({ averagePrice, defaultValue }) => {
  const averageRate = averagePrice ? currencyFormat(averagePrice.average) : '40.00';
  const [selectedRate, setSelectedRate] = useState(averageRate);
  const [disabledBtn, setDisabledBtn] = useState(false);
  const [industrialAverage, setIndustrialAverage] = useState(100.0);
  const [industrialAverageColor, setIndustrialAverageColor] = useState(colors.success[500]);
  const rates = [];
  const minPrice = averagePrice?.min || 28;
  const maxPrice = averagePrice?.max || 72;

  for (let i = minPrice; i <= maxPrice; i += 1) {
    rates.push(i);
  }

  const industrialAverageIcon = {
    [colors.success[500]]: <SolidCheckCircleIcon size={20} color={colors.success[500]} />,
    [colors.warning[700]]: (
      <SolidExclamationWarningAlertIcon size={20} color={colors.warning[500]} />
    ),
    [colors.error[500]]: <SolidExclamationWarningAlertIcon size={20} color={colors.error[500]} />,
  };

  const getIndustrialAverageColor = (percentage) => {
    if (percentage >= 1) {
      return colors.success[500];
    }

    if (percentage >= 0.85 && percentage < 1) {
      return colors.warning[700];
    }

    return colors.error[500];
  };

  const getIndustrialAverageText = () => {
    let message;
    let status;

    if (industrialAverage >= 106) {
      status = 'More likely to fill.';
      message = 'Rate is competitive with the industry average';
    } else if (industrialAverage >= 100 && industrialAverage < 106) {
      status = 'Likely to fill.';
      message = 'Rate is around the industry average';
    } else if (industrialAverage >= 85 && industrialAverage < 100) {
      status = 'Less likely to fill.';
      message = 'Rate is below industry average';
    } else {
      status = 'Unlikely to fill.';
      message = 'Rate is well below industry average';
    }

    return (
      <b
        style={{
          color: colors.neutral[600],
          fontSize: 16,
        }}
      >
        <span style={{ color: industrialAverageColor }}>{status}</span> {message}
      </b>
    );
  };

  useEffect(() => {
    setSelectedRate(averageRate);
    setIndustrialAverage(100.0);
    setIndustrialAverageColor(colors.success[500]);
    setDisabledBtn(false);
  }, [averageRate]);

  const getWarningMessage = () => {
    if (selectedRate < rates[0]) {
      return (
        <span
          className="bold global_font f-dark"
          style={{ color: colors.error[500], fontSize: 16, textAlign: 'center' }}
        >
          Invalid value, too low
        </span>
      );
    }

    if (selectedRate > rates[rates.length - 1]) {
      return (
        <span
          className="bold global_font f-dark"
          style={{ color: Colors.error_600, fontSize: 16, textAlign: 'center' }}
        >
          Invalid value, above maximum
        </span>
      );
    }

    return getIndustrialAverageText();
  };

  const setOfferRate = (newRate) => {
    setSelectedRate(newRate);

    if (newRate < rates[0] || newRate > rates[rates.length - 1]) {
      setDisabledBtn(true);
    } else {
      setDisabledBtn(false);
    }

    const percentage = newRate / averageRate;

    setIndustrialAverage(parseFloat(percentage * 100).toFixed(1));
    setIndustrialAverageColor(getIndustrialAverageColor(percentage));
  };

  const currentIndex = rates.reduce((closestIndex, value, index) => {
    const currentValue = Math.abs(Math.ceil(value - selectedRate));
    const closestValue = Math.abs(Math.ceil(rates[closestIndex] - selectedRate));

    return currentValue < closestValue ? index : closestIndex;
  }, 0);

  useEffect(() => {
    if (defaultValue) {
      setOfferRate(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    selectedRate,
    industrialAverageIcon,
    industrialAverageColor,
    rates,
    getWarningMessage,
    setOfferRate,
    currentIndex,
    disabledBtn,
    setSelectedRate,
    industrialAverage,
  };
};
